import React, { useMemo } from "react";
import { Box, CssBaseline, ThemeProvider } from "@mui/material";
import AppHeader from "./components/AppHeader";
import Explore from "./pages/Explore";
import { GlobalSettingsProvider, useGlobalSettings } from "./GlobalSettingsContext";
import ErrorBoundary from "./components/ErrorBoundary";
import { HEADER_COLORS, buildTheme } from "./theme";

const ThemedApp: React.FC = () => {
  const {
    settings: { themeMode },
  } = useGlobalSettings();
  const theme = useMemo(() => buildTheme(themeMode), [themeMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* The solid page background fills the bottom/overscroll safe areas on its
            own; only the top status bar is overridden to the header color so it
            merges with the AppBar. */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "env(safe-area-inset-top)",
          // Same solid color as the AppBar so the status-bar fill is seamless.
          backgroundColor: HEADER_COLORS[themeMode],
          zIndex: (t) => t.zIndex.appBar + 1,
          pointerEvents: "none",
        }}
      />
      <AppHeader />
      <Box
        component="main"
        sx={{
          paddingLeft: "1rem",
          // Clear the fixed AppBar (4rem) plus the iOS status-bar safe area,
          // which the AppBar itself extends into.
          paddingTop: "calc(4rem + env(safe-area-inset-top))",
          // Keep content above the iOS home indicator when scrolled to the end.
          paddingBottom: "env(safe-area-inset-bottom)",
          minHeight: "100vh",
        }}
      >
        <ErrorBoundary>
          <Explore />
        </ErrorBoundary>
      </Box>
    </ThemeProvider>
  );
};

// The theme mode lives in global settings, so the themed shell must render
// inside the settings provider.
const App: React.FC = () => (
  <GlobalSettingsProvider>
    <ThemedApp />
  </GlobalSettingsProvider>
);

export default App;
